import type { NavIcon, PresetId } from 'providers/HeaderProvider';
import { HeaderContext, resolvePresetConfig } from 'providers/HeaderProvider';
import { useContext, useEffect, useRef } from 'react';

export interface SetHeaderConfigOptions {
  preset?: PresetId;
  hideNavLeft?: boolean;
  navIcon?: NavIcon;
  title?: string;
  subtitle?: string;
  showCloseRight?: boolean;
  onNavigationAction?: () => void;
  onCloseAction?: () => void;
  enabled?: boolean;
}

export function useSetHeaderConfig(options: SetHeaderConfigOptions): void {
  const context = useContext(HeaderContext);

  if (!context) {
    throw new Error('useSetHeaderConfig must be used within HeaderProvider');
  }

  const { pushConfig, popConfig } = context;
  const onNavigationActionRef = useRef(options.onNavigationAction);
  onNavigationActionRef.current = options.onNavigationAction;
  const onCloseActionRef = useRef(options.onCloseAction);
  onCloseActionRef.current = options.onCloseAction;

  const { preset, navIcon, title, subtitle, showCloseRight, hideNavLeft, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return undefined;

    const presetConfig = preset ? resolvePresetConfig(preset) : {};
    const overrides: Record<string, unknown> = {};

    if (hideNavLeft !== undefined) overrides.hideNavLeft = hideNavLeft;
    if (navIcon !== undefined) overrides.navIcon = navIcon;
    if (title !== undefined) overrides.title = title;
    if (subtitle !== undefined) overrides.subtitle = subtitle;
    if (showCloseRight !== undefined) overrides.showCloseRight = showCloseRight;

    const config = {
      ...presetConfig,
      ...overrides,
      ...(onNavigationActionRef.current
        ? {
            onNavigationAction: () => onNavigationActionRef.current?.(),
          }
        : {}),
      ...(onCloseActionRef.current
        ? {
            onCloseAction: () => onCloseActionRef.current?.(),
          }
        : {}),
    };

    const id = pushConfig(config);
    return () => popConfig(id);
  }, [preset, navIcon, title, subtitle, showCloseRight, hideNavLeft, enabled, pushConfig, popConfig]);
}
